import { useEffect, useMemo, useState } from "react";
import { usePrevious } from "react-use";

import { type TableMutation, contentId } from "@moment/api-collab/differential";
import {
	type V1BaseSpec as BaseSpec,
	type V1TableSpec as TableSpec,
	type V1ViewSpec as ViewSpec,
} from "@moment/api-collab/differential/v1-base-spec";

import { useCellServices } from "../CellServicesContext";

const subscriptions = new Map<string, () => void>();

export const useBaseViewQuery = (viewId: string) => {
	const { base, cellId } = useCellServices();

	const [baseSpec, setBaseSpec] = useState<BaseSpec | undefined>(undefined);
	const [tableSpec, setTableSpec] = useState<TableSpec | undefined>(undefined);
	const [viewSpec, setViewSpec] = useState<ViewSpec | undefined>(undefined);
	const table = viewSpec?.query.spec[0].table;
	useEffect(() => {
		if (!base) {
			return;
		}
		const unsubscribe = base.subscribeMetadata((baseSpec) => {
			setBaseSpec(baseSpec);
			setTableSpec(table ? baseSpec.tables[table] : undefined);
			setViewSpec(baseSpec.views[viewId]);
		});
		return () => unsubscribe?.();
	}, [base, viewId, table]);

	const query = viewSpec?.query;

	//
	// IMPORTANT:
	//
	//   - You *MUST* use `cellId` as the subscription ID. You must *NOT* use *ONLY* content ID or
	//     compound key of the query. This is because users often have many cells with the same
	//     query for a variety of reasons; the code below deduplicates them on that key, so only one
	//     will ever receive results.
	//   - You *MUST* set the `subscriptionId` to `undefined` if query is empty, to signal to the
	//     code below when to acquire the subscription.
	//
	//
	const subscriptionId = useMemo(
		() =>
			query
				? cellId +
					"|" +
					contentId(query.spec) +
					"|" +
					query.subquerySpecs.map(contentId).join("|")
				: undefined,
		[query, cellId]
	);

	const [results, setResults] = useState<unknown[]>([]);
	const [changes, setChanges] = useState<TableMutation<unknown>[]>([]);

	const previousId = usePrevious(subscriptionId);
	if (subscriptionId && !subscriptions.has(subscriptionId)) {
		if (previousId && subscriptions.has(previousId)) {
			const unsubscribe = subscriptions.get(previousId);
			unsubscribe?.();
			subscriptions.delete(previousId);
		}
		const unsubscribe = base.subscribeQuery(query, (results, changes) => {
			setResults(results);
			setChanges(changes);
		});
		subscriptions.set(subscriptionId, unsubscribe);
	}

	return { baseSpec, tableSpec, viewSpec, results, changes };
};
